import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

const translationIdPluginKey = new PluginKey('translationIds');
const translatableNodeTypes: Record<string, true> = { heading: true, paragraph: true };

type Candidate = { id: string; position: number };

function translationId(node: { attrs: Record<string, unknown> }): string | undefined {
  const id = node.attrs.translationId;
  return typeof id === 'string' && id ? id : undefined;
}

/** Persists one stable identifier per translatable master-content block. */
export const TranslationIds = Extension.create({
  name: 'translationIds',

  addGlobalAttributes() {
    return [
      {
        types: ['heading', 'paragraph'],
        attributes: {
          translationId: {
            default: null,
            renderHTML: () => ({}),
          },
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: translationIdPluginKey,
        appendTransaction(transactions, oldState, newState) {
          if (!transactions.some(transaction => transaction.docChanged || transaction.getMeta(translationIdPluginKey)))
            return null;

          const retainedPositions = new Set<number>();
          oldState.doc.descendants((node, position) => {
            if (!translatableNodeTypes[node.type.name]) return;
            const id = translationId(node);
            if (!id) return;
            let mappedPosition = position;
            let deleted = false;
            for (const transaction of transactions) {
              const result = transaction.mapping.mapResult(mappedPosition);
              mappedPosition = result.pos;
              if (result.deleted) {
                deleted = true;
                break;
              }
            }
            if (!deleted && translationId(newState.doc.nodeAt(mappedPosition) ?? node) === id)
              retainedPositions.add(mappedPosition);
          });

          const candidates = new Map<string, Candidate[]>();
          const missing: number[] = [];
          newState.doc.descendants((node, position) => {
            if (!translatableNodeTypes[node.type.name]) return;
            const id = translationId(node);
            if (!id) {
              missing.push(position);
              return;
            }
            const entries = candidates.get(id) ?? [];
            entries.push({ id, position });
            candidates.set(id, entries);
          });

          const replacements = new Set<number>(missing);
          for (const entries of candidates.values()) {
            if (entries.length < 2) continue;
            const preserved = entries.find(entry => retainedPositions.has(entry.position)) ?? entries[0];
            entries.forEach(entry => {
              if (entry.position !== preserved.position) replacements.add(entry.position);
            });
          }
          if (!replacements.size) return null;

          const transaction = newState.tr;
          replacements.forEach(position => {
            const node = newState.doc.nodeAt(position);
            if (node)
              transaction.setNodeMarkup(position, undefined, { ...node.attrs, translationId: crypto.randomUUID() });
          });
          return transaction;
        },
      }),
    ];
  },

  onCreate() {
    this.editor.view.dispatch(this.editor.state.tr.setMeta(translationIdPluginKey, true));
  },
});
